import { fetchAPI } from "./utils";

// Helper function to get auth token from localStorage
const getAuthToken = (): string | null => {
    return localStorage.getItem("token");
};

export interface BotCreateRequest {
    name: string;
    code?: string;
    tags?: {
        [key: string]: string;
    };
    configuration?: {
        [key: string]: any;
    };
    states?: {
        [key: string]: any;
    };
    events?: {
        [key: string]: any;
    };
    actions?: {
        [key: string]: any;
    };
    medias?: Array<{
        id: string;
        url: string;
        type: string;
    }>;
    dev?: boolean;
    integrations?: {
        [key: string]: {
            configuration: WebchatConfiguration;
            enabled: boolean;
            name?: string;
        };
    };
}

export interface WebchatConfiguration {
    version?: string;
    botDisplayName?: string;
    botAvatarUrl?: string;
    botDescription?: string;
    descriptionPhoneNumber?: {
        title: string;
        link: string;
    };
    descriptionEmailAddress?: {
        title: string;
        link: string;
    };
    descriptionWebsiteUrl?: {
        title: string;
        link: string;
    };
    primaryColor?: string;
    themeMode?: "light" | "dark";
    instructions?: string;
    trainingFiles?: string[];
}

export interface Bot {
    id: string;
    name: string;
    description?: string;
    tags?: {
        [key: string]: string;
    };
    createdAt: string;
    updatedAt: string;
    dev?: boolean;
    url?: string;
    integrations?: {
        [key: string]: {
            configuration: WebchatConfiguration;
            enabled: boolean;
            name: string;
        };
    };
}

export interface BotResponse {
    bot: Bot;
}

export interface BotListResponse {
    bots: Bot[];
    meta: {
        count: number;
    };
}

// Files API interfaces
export interface BotpressFile {
    id: string;
    key: string;
    size: number;
    url: string;
    uploadUrl?: string;
    status: "upload_pending" | "indexing_pending" | "indexing_completed" | "indexing_failed";
    failedStatusReason?: string;
    tags?: Record<string, string>;
    accessPolicies?: string[];
    contentType?: string;
    expiresAt?: string;
    metadata?: any;
    createdAt: string;
    updatedAt: string;
}

export interface FileUploadRequest {
    key: string;
    size: number;
    tags?: Record<string, string>;
    index?: boolean;
    accessPolicies?: string[];
    contentType?: string;
    expiresAt?: string;
    publicContentImmediatelyAccessible?: boolean;
    metadata?: any;
}

export interface FileListResponse {
    files: BotpressFile[];
    nextToken?: string;
}

export interface FileSearchResponse {
    data: {
        passages: Array<{
            content: string;
            fileId: string;
            score: number;
            start: number;
            end: number;
        }>;
    };
}

// Create a new bot
export const createBot = async (data: BotCreateRequest): Promise<Bot> => {
    const token = getAuthToken();
    if (!token) {
        throw new Error("Authentication token not found");
    }

    return fetchAPI<Bot>("/api/bots", "POST", data, token);
};

// List all bots
export const listBots = async (): Promise<BotListResponse> => {
    const token = getAuthToken();
    if (!token) {
        throw new Error("Authentication token not found");
    }

    return fetchAPI<BotListResponse>("/api/bots", "GET", undefined, token);
};

// Get a specific bot
export const getBot = async (botId: string): Promise<BotResponse> => {
    const token = getAuthToken();
    if (!token) {
        throw new Error("Authentication token not found");
    }

    return fetchAPI<BotResponse>(`/api/bots/${botId}`, "GET", undefined, token);
};

// Update a bot
export const updateBot = async (botId: string, data: Partial<BotCreateRequest>): Promise<Bot> => {
    const token = getAuthToken();
    if (!token) {
        throw new Error("Authentication token not found");
    }

    return fetchAPI<Bot>(`/api/bots/${botId}`, "PUT", data, token);
};

// Update webchat configuration
export const updateWebchatConfiguration = async (
    botId: string,
    configuration: WebchatConfiguration,
    webchatIntegrationId?: string,
): Promise<Bot> => {
    const token = getAuthToken();
    if (!token) {
        throw new Error("Authentication token not found");
    }

    return fetchAPI<Bot>(
        `/api/bots/${botId}/webchat`,
        "PUT",
        {
            configuration,
            webchatIntegrationId,
        },
        token,
    );
};

// Helper function to create or update bot with webchat configuration
export const createOrUpdateBotWithWebchat = async (
    botId: string | null,
    botData: Partial<BotCreateRequest>,
    webchatConfig: WebchatConfiguration,
    webchatIntegrationId?: string,
): Promise<Bot> => {
    const token = getAuthToken();
    if (!token) {
        throw new Error("Authentication token not found");
    }

    return fetchAPI<Bot>(
        "/api/bots/create-or-update-with-webchat",
        "POST",
        {
            botId,
            botData,
            webchatConfig,
            webchatIntegrationId,
        },
        token,
    );
};

// Delete a bot
export const deleteBot = async (botId: string): Promise<void> => {
    const token = getAuthToken();
    if (!token) {
        throw new Error("Authentication token not found");
    }

    return fetchAPI<void>(`/api/bots/${botId}`, "DELETE", undefined, token);
};

// Get account information
export const getBotpressAccount = async () => {
    const token = getAuthToken();
    if (!token) {
        throw new Error("Authentication token not found");
    }

    return fetchAPI("/api/account/me", "GET", undefined, token);
};

// Files API functions
export const createFile = async (botId: string, fileData: FileUploadRequest): Promise<{ file: BotpressFile }> => {
    const token = getAuthToken();
    if (!token) {
        throw new Error("Authentication token not found");
    }

    return fetchAPI<{ file: BotpressFile }>(`/api/files/${botId}`, "PUT", fileData, token);
};

// Note: This function is deprecated in favor of the S3 -> Backend -> Botpress flow
// Use uploadFileFromUrl instead after uploading to S3 first
export const uploadFileContent = async (
    uploadUrl: string,
    content: Uint8Array | string,
    isPublic: boolean = false,
): Promise<void> => {
    throw new Error("uploadFileContent is deprecated. Use S3 upload followed by uploadFileFromUrl instead.");
};

export const listFiles = async (
    botId: string,
    tags?: Record<string, string>,
    nextToken?: string,
): Promise<FileListResponse> => {
    const token = getAuthToken();
    if (!token) {
        throw new Error("Authentication token not found");
    }

    const params = new URLSearchParams();
    if (nextToken) params.append("nextToken", nextToken);
    if (tags) params.append("tags", JSON.stringify(tags));

    const endpoint = `/api/files/${botId}${params.toString() ? `?${params.toString()}` : ""}`;
    return fetchAPI<FileListResponse>(endpoint, "GET", undefined, token);
};

export const getFile = async (botId: string, fileId: string): Promise<{ file: BotpressFile }> => {
    const token = getAuthToken();
    if (!token) {
        throw new Error("Authentication token not found");
    }

    return fetchAPI<{ file: BotpressFile }>(`/api/files/${botId}/${fileId}`, "GET", undefined, token);
};

export const updateFile = async (
    botId: string,
    fileId: string,
    updates: {
        tags?: Record<string, string | null>;
        accessPolicies?: string[];
    },
): Promise<{ file: BotpressFile }> => {
    const token = getAuthToken();
    if (!token) {
        throw new Error("Authentication token not found");
    }

    // Note: Backend expects PATCH method but fetchAPI only supports GET, POST, PUT, DELETE
    // The backend should handle PUT method for updates as well
    return fetchAPI<{ file: BotpressFile }>(`/api/files/${botId}/${fileId}`, "PUT", updates, token);
};

export const deleteFile = async (botId: string, fileId: string): Promise<void> => {
    const token = getAuthToken();
    if (!token) {
        throw new Error("Authentication token not found");
    }

    return fetchAPI<void>(`/api/files/${botId}/${fileId}`, "DELETE", undefined, token);
};

export const searchFiles = async (
    botId: string,
    query: string,
    options?: {
        contextDepth?: number;
        tags?: Record<string, string>;
        limit?: number;
    },
): Promise<FileSearchResponse> => {
    const token = getAuthToken();
    if (!token) {
        throw new Error("Authentication token not found");
    }

    return fetchAPI<FileSearchResponse>(
        `/api/files/${botId}/search`,
        "POST",
        {
            query,
            contextDepth: options?.contextDepth || 0,
            tags: options?.tags,
            limit: options?.limit || 20,
        },
        token,
    );
};

// New function to upload file from URL (for streaming from S3)
export const uploadFileFromUrl = async (
    botId: string,
    fileData: {
        key: string;
        url: string;
        size: number;
        tags?: Record<string, string>;
        accessPolicies?: string[];
        contentType?: string;
    },
): Promise<{ file: BotpressFile }> => {
    const token = getAuthToken();
    if (!token) {
        throw new Error("Authentication token not found");
    }

    return fetchAPI<{ file: BotpressFile }>(`/api/files/${botId}/upload-from-url`, "POST", fileData, token);
};

// New function to list files by chatbot tag
export const listFilesByChatbotTag = async (botId: string, nextToken?: string): Promise<FileListResponse> => {
    const token = getAuthToken();
    if (!token) {
        throw new Error("Authentication token not found");
    }

    const params = new URLSearchParams();
    if (nextToken) params.append("nextToken", nextToken);

    const endpoint = `/api/files/${botId}/by-chatbot-tag${params.toString() ? `?${params.toString()}` : ""}`;
    return fetchAPI<FileListResponse>(endpoint, "GET", undefined, token);
};

// New function to list files by knowledge base tag
export const listFilesByKnowledgeBase = async (
    botId: string,
    kbId: string = "kb-default",
    nextToken?: string,
): Promise<FileListResponse> => {
    const token = getAuthToken();
    if (!token) {
        throw new Error("Authentication token not found");
    }

    const params = new URLSearchParams();
    if (nextToken) params.append("nextToken", nextToken);
    if (kbId !== "kb-default") params.append("kbId", kbId);

    const endpoint = `/api/files/${botId}/by-knowledge-base${params.toString() ? `?${params.toString()}` : ""}`;
    return fetchAPI<FileListResponse>(endpoint, "GET", undefined, token);
};
